import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {formatLocal} from "../util/Formatters";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        IconButton, Paper, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {ArrowBackOutlined, EditOutlined} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router";
import SbomPane from "../components/Object/SbomPane";
import CustomerReleasesPane from "../components/Object/CustomerReleasesPane";
import QuoteLinesPane from "../components/Object/QuoteLinesPane";
import QuoteOfferPane from "../components/Object/QuoteOfferPane";
import DiscussionPane from "../components/Object/DiscussionPane";
import BuildPlanPane from "../components/Object/BuildPlanPane";
import BuildPlanWorkforcePane from "../components/Object/BuildPlanWorkforcePane";
import QualityRecordPane from "../components/Object/QualityRecordPane";
import QuestionnairePane from "../components/Object/QuestionnairePane";
import ContractSchedulePane from "../components/Object/ContractSchedulePane";
import InventoryLotsPane from "../components/Object/InventoryLotsPane";
import ReceiptLotsPane from "../components/Object/ReceiptLotsPane";
import ItemSuppliersPane from "../components/Object/ItemSuppliersPane";
import ItemBillOfMaterialsPane from "../components/Object/ItemBillOfMaterialsPane";
import OrderReceiptsPane from "../components/Object/OrderReceiptsPane";
import DevelopmentPane from "../components/Object/DevelopmentPane";
import QuoteRequestPane from "../components/Object/QuoteRequestPane";
import SupplierDocumentsPane from "../components/Object/SupplierDocumentsPane";
import RecordRail from "../components/Common/RecordRail";
import CopyableValue from "../components/Common/CopyableValue";
import {defaultStatusesFor} from "../util/objectStatuses";
import EnterpriseObject from "../eoec/eo/EnterpriseObject";
import enterpriseObjectClassFactory from "../eoec/EnterpriseObjectClassFactory";
import ObjectAuditHistoryTableApplet from "../eoec/applet/impl/Foundation/ObjectAuditHistoryTableApplet";
import ObjectRelationshipTableApplet from "../eoec/applet/impl/Foundation/ObjectRelationshipTableApplet";
import ObjectRelatedToTableApplet from "../eoec/applet/impl/Foundation/ObjectRelatedToTableApplet";
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";
import QuizzesTab from "../components/Training/QuizzesTab";
import {saveTrainingSetup} from "../data/quiz";
import TrainingCurriculumPanel from "../components/Training/TrainingCurriculumPanel";
import {hasPrivilege, useSessionContext} from "../util/SessionContext";
import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import {ObjectClassification, classificationByCode, objectPrivilegeCode,
        objectPrivilegeNames} from "../util/objectClassifications";
import ObjectVisibilityTab from "../components/Foundation/ObjectVisibilityTab";
import {useUnsavedChanges} from "../util/UnsavedChanges";
import SecondaryOwners from "../components/Foundation/SecondaryOwners";
import ObjectFormTab from "../components/Forms/ObjectFormTab";
import ApprovalTab from "../components/Approvals/ApprovalTab";
import ProjectFiling from "../components/Object/ProjectFiling";
import RecordFolderField from "../components/Object/RecordFolderField";
import Attachments from "../components/Common/Attachments";
import ParkourDocuments from "../components/Foundation/ParkourDocuments";
import ProjectMembers from "../components/Foundation/ProjectMembers";
import OverflowTabs from "../components/Common/OverflowTabs";
import {KIND_ICON, KIND_LABEL, MEMBER_KINDS, fileableKind} from "../util/projectMembers";
import {scopeGridTo, seedGridWith} from "../util/scopeGrid";
import PermissionConfiguration from "../components/Foundation/PermissionConfiguration";
import {usePrivilegeRoles} from "../util/usePrivilegeRoles";
import {Blue3, Grey17, Grey2, Grey5, Grey6, Grey7, White} from "../theme/styles";
import {statusPill} from "../util/statusPill";
import LifecycleActions, {REVISION_ACTIONS} from "../components/Common/LifecycleActions";

// Today in the READER'S zone, as YYYY-MM-DD. toISOString() would give today in UTC, which for
// anyone west of Greenwich in the evening is TOMORROW -- a date picker whose minimum is tomorrow
// refuses the user's own today. Built from the local parts for that reason.
function localToday(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
        + `-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * One first-class object, at "<route>/:usid". The USID is an ObjectRevision USID, not the logical
 * object's -- a record has many revisions and the page has to know which one it is showing.
 *
 * Five tabs, mirroring Term and Rule in the Data Catalog: Detail, Visibility, Approvals,
 * Permissions, History.
 *
 * The classification passed in decides the labels and the back route. The record's OWN
 * ObjectClassification is preferred once loaded, so following a link to a record of a different
 * classification shows that one's identity rather than the page's.
 */

const BASE_PANES = ["Detail", "Visibility", "Approvals", "Permissions", "History"];
// THE TWO RELATIONSHIP GRIDS AS TABS OF THEIR OWN (Ed, 2026-10-28: "universally make Related
// objects and Related to this tables tabs of their own on their respective detail pages"). They
// sat under the Detail card, which made every record's first screen as long as its edges; a tab
// each puts them where a reader goes for them and nowhere else.
const RELATED_PANE = "Related objects";
const RELATED_TO_PANE = "Related to this";

// The Form pane appears only for a record that WAS seeded from one -- a record with no shape has no
// questions to draw, and an empty tab is worse than no tab. Inserted after Detail because the answers
// are the record's content; the rest of the tabs are about the record.
const FORM_PANE = "Form";

// Files attached to the record ITSELF, as opposed to files answering a File field on its form. Both
// are attachments and both come back from the same API; what separates them is a field code, and
// this pane is the ones with none. Always offered -- unlike Form, a record does not need a shape to
// have a document attached to it.

// The authored TEXT of this document, written in Docs. A third kind of content beside the form's
// answers and the record's files, and not a substitute for either: a record can have a shape, a pile
// of attachments and a procedure written in Docs, and most have some but not all.
//
// ALWAYS OFFERED, like Attachments and unlike Form. Whether anything has been authored is not a
// property of the record the way "was seeded from a shape" is -- it is a thing somebody does later,
// and a tab that appeared only once content existed would give nobody a way to find out they could
// create some.
// "Parkour AI Documents", the product's name in full. Ed, 2026-10-29: "On each detail page, the
// tab named \"Parkour Documents\" should be named \"Parkour AI Documents\"."
const PARKOUR_PANE = "Parkour AI Documents";
// CONDITIONAL, LIKE THE QUIZ PANE. Most records in a quality system will never have a bill
// of materials, and a tab that is always present and usually empty is one people learn to
// ignore. It appears only where the SBOM application holds one for THIS revision.
const SBOM_PANE = "SBOM";

/**
 * WHAT IS FILED IN A PROJECT. The one thing a folder has that no other record does.
 *
 * ONE PANE WITH FIVE INNER TABS rather than five panes on this strip, which already offers seven or
 * more: twelve would bury the ones every record has. The counts live on the inner tabs, which is
 * where they were asked for -- "Products 0" beside "Quote Requests 6".
 *
 * IT IS CONTENT, so it sits with the content panes and before the ones that are ABOUT the record.
 */
//: THERE IS NO "CONTENTS" TAB. Ed's rule of 2026-10-27: "put the Contents tab below the top set of
//: tabs into tabs of their own". A folder's contents were two clicks away -- Contents, then the
//: kind -- and the counts were invisible until the first of those.
//:
//: AND THE KINDS ARE NOT PANES. They were, briefly, so that one selection could span both rows --
//: but choosing a kind then deselected the top row and took the record's own fields off the screen,
//: so reading what was filed in a project cost you the project. ProjectMembers owns that selection
//: now and the two rows are independent, which is what the screen already looked like it meant.

// Training, phase 1. OFFERED ONLY WHEN THE RECORD REQUIRES TRAINING, which the Detail tab's own switch
// decides. It was briefly always-on because Training Setup carried a master "Require Training" toggle and
// that switch cannot live behind the tab it reveals -- moving the flag onto Detail, where a property of
// the record belongs, dissolves the circle and lets the tab say what the design said it says.
const QUIZ_PANE = "Quizzes";

// WHO OUTSIDE THIS COMPANY HAS BEEN GIVEN THIS LOT. Offered for Inventory and nothing else, by
// classification rather than by whether any release exists -- and that difference from the SBOM
// tab beside it is the point. A tab that appeared only once a lot had been released would give
// nobody a way to find out they could release one; a tab that appeared on a Document would be
// offering an act that has no meaning there.
//
// IT IS ALSO THE RECALL QUERY. "Which customers hold batch 24-0117" has no other answer in this
// estate, so the tab has to exist on every lot, including the ones nothing has gone out from yet.
const CUSTOMERS_PANE = "Customers";
// A LOT'S STOCK, IN TWO TABS (Ed, 2026-10-30: "Lots needs to have lotTransfers and cycleCount").
// Transfers is the inventory ledger with where and for what; Cycle counts is what was found on the
// shelf against it. Both on every lot, like Customers, because a lot nothing has moved yet is the
// one somebody is about to receive.

// WHAT THE OFFER IS MADE OF AND WHAT IT COMES TO. Offered for PreparedQuote and nothing else, by
// classification rather than by whether the quote has been priced yet -- the same decision as
// Customers above and for the same reason: a tab that appeared only once lines existed would give
// nobody a way to find out they could add one. A new quote's first state is an empty offer with no
// currency, and that state needs a surface more than a priced one does.
//
// IT IS THE FIRST TAB AFTER Detail, ahead of the files and the authored text. A quote's lines ARE
// the record in a way an attachment is not: everything else on this page is about the offer, and
// this is the offer.
const QUOTE_PANE = "Lines & totals";

// THE ROUTE AND THE NUMBER THAT FOLLOWS FROM IT. Offered for BuildPlan and nothing else.
const PLAN_PANE = "Steps & schedule";

// WHETHER THE PEOPLE ON THE PLAN CAN ACTUALLY BUILD IT, and a SEPARATE tab from the route on
// purpose. The service splits them for the same reason -- `Get Build Plan Steps` and `Get Build
// Plan Workforce` are different answers to different questions, and the t3 endpoint refuses to
// serve more than one shape per request because a payload whose fields depended on several flags
// is one a caller can only read half of. A single tab carrying both would be that page.
//
// AND THEY ARE READ AT DIFFERENT MOMENTS. The route is edited; the workforce answer is ASKED, and
// re-asked, because qualification decays on dates nobody triggers. Putting them together would
// imply the second is as stable as the first.
const PLAN_WORKFORCE_PANE = "Workforce";

// AN ISSUE OR A CAPA: what it says, where it is in its own workflow, what it caused, and who did
// what to it. ONE CLASSIFICATION HOLDS BOTH KINDS -- an Issue (ISS-) and a CAPA (CAPA-) are the
// same classification with different object TYPES -- so one pane serves both, which is correct and
// is also the thing a reader assumes wrongly.
const QUALITY_PANE = "Workflow & actions";

// THE QUESTIONS, THEIR ANSWERS, AND WHERE EACH ANSWER CAME FROM. Offered for Questionnaire and
// nothing else. The source beside every answer is the phase rather than a decoration: the library,
// this company's documents, or a person -- and no fourth.
const QUESTIONNAIRE_PANE = "Questions & answers";

// WHAT WAS ACTUALLY AGREED: the lines, their milestones and the payments those trigger. Offered
// for Contract and nothing else. Everything dated on it is computed when the page is read and
// stored nowhere, which is the decision the whole phase turns on.
const CONTRACT_PANE = "Schedule & payments";
// AN INVENTORY IS A COLLECTION OF LOTS (Ed, 2026-10-30), held by edges and read per lot under the
// reader's own sight: the pane greys a lot they may not open. Not a form field -- a lot is a record.
const INVENTORY_LOTS_PANE = "Lots";
// A RECEIPT BECOMES LOTS (phase 3, 2026-10-30): the pane lists them under the reader's sight and
// makes one from a line of a released receipt.
const RECEIPT_LOTS_PANE = "Lots";
// AN ITEM'S SUPPLIERS (phase 4, 2026-10-30): read off the suppliers' own Supplied Items, under the
// reader's sight. Edited on the supplier, never here.
const ITEM_SUPPLIERS_PANE = "Suppliers";
// AN ORDER'S RECEIPTS (Ed, 2026-10-30: "Add a Receipts list on the PO page"), each under the reader's sight.
const ORDER_RECEIPTS_PANE = "Receipts";
// THE MULTI-LEVEL BILL OF MATERIALS (Ed, 2026-10-30): explosion down, where-used up, on an Item.
const ITEM_BOM_PANE = "Bill of materials";

// WHAT WE HAVE ASKED THIS SUPPLIER FOR AND WHAT CAME BACK. Offered for Supplier and nothing else.
// THREE answers about expiry rather than two -- a certificate nobody has read a date off is
// neither current nor expired, and collapsing it into either produces a false clear.
const SUPPLIER_PANE = "Documents & expiry";

// WHAT A PRODUCT IS CLAIMED TO DO, OR WHAT AN EXPERIMENT FOUND -- and why each process was chosen.
// ONE PANE NAME FOR TWO SPECIES because the page has the same three parts either way: the record's
// own fields, the design decisions behind it, and the reason attached to each. The selected
// processes are genuinely one thing -- one table, one document -- so two components would drift.
const DEVELOPMENT_PANE = "Claims & decisions";

// THE DEMAND ITSELF: who asked, what arrived, what is wanted, and where it is in triage. Offered
// for QuoteRequest and nothing else. A request became the nineteenth species on 2026-10-27 and
// its own fields stayed on a satellite table -- the triage queue filters and computes with them
// -- so the standard panes above show the record and this one shows the request. Without it the
// page would carry the subject as a name and the body as a description and nothing else that was
// asked, which is the fact-recorded-and-not-carried-to-the-reader shape, one more time.
const REQUEST_PANE = "Request";

const tabStyle = (active: boolean): React.CSSProperties => ({
    cursor: "pointer",
    paddingBottom: 6,
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? Grey17 : Grey6,
    borderBottom: active ? `2px solid ${Blue3}` : "2px solid transparent",
});

function Field({label, value}: {label: string; value: any}) {
    // A rendered value -- the status pill -- is placed as-is. Wrapping an element in the Typography
    // below would put a chip inside a paragraph and inherit its font size onto the chip's label.
    // The row keeps the same height either way, so a field of pills lines up with a field of text.
    const rendered = React.isValidElement(value);

    return (
        // THE SAME READING SURFACE AS t5, so a record reads the same way in both apps: a quiet grey
        // label over a dark value, in the NavBar's idiom. The label was uppercase, bold and
        // letter-spaced, which competes with the value it introduces -- a label is signposting, and
        // the value is the thing somebody came to read.
        <div style={{display: "flex", flexDirection: "column", gap: 1, minWidth: 220}}>
            <Typography style={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>
                {label}
            </Typography>
            {rendered ? (
                <div style={{display: "flex", alignItems: "center", minHeight: 21}}>{value}</div>
            ) : (
                <Typography style={{fontSize: 15, fontWeight: 400, color: Grey17, lineHeight: 1.45,
                                    overflowWrap: "anywhere"}}>{value || "—"}</Typography>
            )}
        </div>
    );
}

export default function ObjectDetail({classification}: {classification: ObjectClassification}) {
    const {usid} = useParams();
    const navigate = useNavigate();
    const sessionContext = useSessionContext();

    const [pane, setPane] = useState<string>("Detail");
    // A PANE CHOSEN FOR ANOTHER RECORD, held until that record has loaded. The rail's tree lets a
    // reader pick "Contracts" under a project that is not the open one; setting the pane and
    // navigating is not enough, because while the new record loads the pane list is the loading
    // page's, "Contract" is not in it, and the fallback below sends the choice back to Detail.
    const pendingPane = useRef<{usid: string; pane: string} | null>(null);
    // THE OFFER'S ANSWERS RE-READ when the quote's line grid changes a line or the terms.
    const [quoteTick, setQuoteTick] = useState(0);
    //: What a project holds, per kind, for the tab labels. ProjectMembers owns the fetch.
    const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
    const [sbomPresence, setSbomPresence] = useState<any | null>(null);
    const [record, setRecord] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<string | null>(null);
    const [savingTraining, setSavingTraining] = useState(false);
    const [askingRetrain, setAskingRetrain] = useState(false);
    const [askingEffective, setAskingEffective] = useState(false);
    // EMPTY MEANS IMMEDIATELY, which is the ordinary release and wants no typing.
    const [effectiveChoice, setEffectiveChoice] = useState<string>("");
    // What the effective step collected, held while the retraining question is answered.
    const [pendingRelease, setPendingRelease] = useState<Record<string, any>>({});
    // The action somebody asked for while a tab still held unsaved edits, parked until they answer.
    const [unsavedWarning, setUnsavedWarning] = useState<
        {action: string; extra: Record<string, any>; tabs: string[]} | null>(null);
    const pendingUnsavedChanges = useUnsavedChanges();
    const [reloadTick, setReloadTick] = useState<number>(0);
    // The object's draft, when one is in flight. Declared with the rest of the page state because
    // availableActions reads it, and that memo is built further up than the loaders below.
    const [draftUSID, setDraftUSID] = useState<string | null>(null);

    // EDITING THE RECORD'S OWN FIELDS, which this page could not do at all until 2026-10-27.
    // Ed: "On any object detail page that is in DRAFT, where is the edit button?" -- there was
    // none. Everything on the Detail pane was read-only on every status, so a Name or a
    // Description was set once in the create dialog and fixed for the life of the record. The
    // SERVER was never the thing missing: ObjectRevision declares Name without `no_update` and
    // `pre_write_record_event_object_revision` already enforces a DRAFT-only edit rule, so the
    // write this opens is the one the backend has been waiting for.
    const [editing, setEditing] = useState(false);
    const [draftTitle, setDraftTitle] = useState("");
    const [draftDescription, setDraftDescription] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [editRefusal, setEditRefusal] = useState<string | null>(null);

    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    // Prefer the record's OWN classification: a relationship link can land here on a record of a
    // different kind, and the header, the privileges and the back route should all name what is
    // actually on screen. Resolved before the hooks below, which depend on it.
    const effective = classificationByCode(record?.ObjectClassification) ?? classification;

    // A record carries the shape it was captured against only when it was seeded from a form. Every
    // object type without one still creates records, and those simply have no Form tab.
    const hasForm = !!record?.RecordTypeRevisionUSID;

    // ADVANCING IS OFFERED WHILE THERE IS SOMEWHERE TO GO. Stage 3 is the last one in every account,
    // so a record already there cannot advance. A two-stage account never puts a record at stage 2 --
    // its only move is 1 to 3 -- so "not yet at 3" is the right test for both shapes, and the server
    // decides which stage is actually next.
    const availableActions = useMemo(() => {
        const stage = Number(record?.RevisionStage ?? 1);
        let actions = REVISION_ACTIONS;

        if (stage >= 3) {
            actions = {
                ...actions,
                DRAFT: (actions.DRAFT ?? []).filter(a => a.action !== "AdvanceStage"),
            };
        }

        // NEITHER MOVE IS OFFERED WHILE A DRAFT IS OPEN. Revise would be refused outright -- an
        // object may have only one draft at a time -- and offering it was how a reader ended up
        // asking for a second one and getting an error for a revision they had already made.
        // Deprecate is withdrawn for the same reason it is refused on the server now: retiring the
        // revision a draft was raised from leaves that draft to release onto a predecessor nobody
        // is meant to be using.
        if (draftUSID && draftUSID !== usid) {
            actions = {
                ...actions,
                RELEASED: (actions.RELEASED ?? [])
                    .filter(a => a.action !== "Revise" && a.action !== "Deprecate"),
            };
        }

        return actions;
    }, [record?.RevisionStage, draftUSID, usid]);

    // THE FORM THIS RECORD WAS CAPTURED UNDER, as a person would cite it: "PO-FORM-05009 REV 1".
    //
    // The record carries only SeededFromRevisionUSID, a revision USID, which identifies the form to
    // the machine and to nobody else. Resolving it to the form's own number and revision is what
    // makes the Detail tab able to answer "which form is this, and which version of it" without
    // opening the designer.
    //
    // The REVISION matters as much as the number. A form is revised, each revision asks different
    // questions, and every record keeps a copy of the one it was captured under -- so citing the
    // form without its revision names something that may no longer look like what was filled in.
    // WHETHER THE SBOM APPLICATION HOLDS ONE FOR THIS REVISION. This is what decides the SBOM tab
    // exists at all, which is the whole of its job now: a chip beside the revision said the same
    // thing one line earlier and was removed, because a tab that only appears when there is one
    // already answers "is there a bill of materials" without spending header space on it.
    //
    // IT CANNOT FAIL LOUDLY. The service answers 200 with Present: false for every outcome it
    // knows about, including a caller the SBOM application refuses; anything that reaches the
    // catch leaves the page exactly as it would be for a record that has no bill of materials.
    useEffect(() => {
        if (!usid) return;
        let cancelled = false;
        // CLEARED FIRST, because this component stays mounted while the sidebar moves between
        // records. Without it the previous record's answer decides whether THIS record offers an
        // SBOM tab, for as long as the new request takes.
        setSbomPresence(null);

        api.get("t1/v1/sbom_presence", {params: {RevisionUSID: usid}})
            .then((response: any) => { if (!cancelled) setSbomPresence(response?.data ?? null); })
            .catch(() => { if (!cancelled) setSbomPresence(null); });

        return () => { cancelled = true; };
    }, [usid, api]);

    const [seededForm, setSeededForm] = useState<any>(null);

    useEffect(() => {
        const seededFrom = record?.SeededFromRevisionUSID;
        if (!seededFrom) {
            setSeededForm(null);
            return;
        }

        let cancelled = false;
        api.get("t1/v1/object_revisions", {params: {USID: seededFrom}})
            .then((response: any) => {
                if (cancelled) return;
                const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
                const rows = Array.isArray(list) ? list : [list];
                setSeededForm(rows[0] ?? null);
            })
            .catch(() => {
                // A form that cannot be read is not an error on this page -- the record is still
                // whole. The field falls back to the dash every other field uses for absent.
                if (!cancelled) setSeededForm(null);
            });

        return () => { cancelled = true; };
        // `api` is memoised on sessionContext, so declaring it re-runs this only when the session
        // itself changes -- which is exactly when a re-fetch is wanted anyway.
    }, [record?.SeededFromRevisionUSID, api]);
    const trainingRequired = record?.TrainingRequired === true;
    const quizPane = trainingRequired ? [QUIZ_PANE] : [];
    const sbomPane = sbomPresence?.Present ? [SBOM_PANE] : [];
    // BY CLASSIFICATION, not by whether a release exists -- see the constant.
    // "Lot" since 2026-10-30: a customer release names a lot, and the lot is its own species now.
    // Transfers and cycle counts are TABLES ON THE LOT FORM (Ed, 2026-10-30: "form based data
    // structures for lots. Cannot be tabs"), so the Form tab draws them and no pane does.
    const customersPane = effective.code === "Lot" ? [CUSTOMERS_PANE] : [];
    // SAME RULE, SAME REASON. See QUOTE_PANE.
    const quotePane = effective.code === "PreparedQuote" ? [QUOTE_PANE] : [];
    const planPanes = effective.code === "BuildPlan" ? [PLAN_PANE, PLAN_WORKFORCE_PANE] : [];
    const qualityPane = effective.code === "QualityRecord" ? [QUALITY_PANE] : [];
    const questionnairePane = effective.code === "Questionnaire" ? [QUESTIONNAIRE_PANE] : [];
    const contractPane = effective.code === "Contract" ? [CONTRACT_PANE] : [];
    const inventoryPane = effective.code === "Inventory" ? [INVENTORY_LOTS_PANE] : [];
    const receiptPane = effective.code === "Receivable" ? [RECEIPT_LOTS_PANE] : [];
    const itemSuppliersPane = effective.code === "Item" ? [ITEM_BOM_PANE, ITEM_SUPPLIERS_PANE] : [];
    const orderReceiptsPane = effective.code === "PurchaseOrder" ? [ORDER_RECEIPTS_PANE] : [];
    const supplierPane = effective.code === "Supplier" ? [SUPPLIER_PANE] : [];
    // EXPERIMENTS ONLY, since 2026-10-29: a product is an Item of type PRT- and its fields are its
    // Form tab, drawn like every item's.
    const developmentPane = effective.code === "Experiment" ? [DEVELOPMENT_PANE] : [];
    const requestPane = effective.code === "QuoteRequest" ? [REQUEST_PANE] : [];
    // A PROJECT IS A CONTAINER AS WELL AS A RECORD, and this is the container half. Everything else
    // on this page is what a project has in common with every other classification -- which is the
    // whole point of it being the eighteenth species rather than a page of its own.
    const isProject = effective.code === "Project";
    // The customer edge, when the record carries one. See the Customer field below. A folder's is
    // 'Project Customer', a contract's 'Contract Customer' -- two relationship types, one meaning.
    // Until 2026-10-29 only the contract's was read, so a project's Customer field never drew.
    // A PROJECT'S DOCUMENT DOES NOT CARRY ITS EDGES (the flat project document says so in its own
    // docstring), so for a folder the edge is read by itself, by parent object -- the same read the
    // rail makes for the whole page of projects, for one.
    const [fetchedCustomerEdge, setFetchedCustomerEdge] = useState<any>(null);
    useEffect(() => {
        const parent = isProject ? record?.ObjectUSID : null;
        if (!parent) { setFetchedCustomerEdge(null); return; }
        let live = true;
        api.get("t1/v1/object_relationships", {params: {ParentUSID: String(parent), Limit: 100},
                                               headers: {"idoc-response": "v1.ObjectRelationship-001"}})
            .then((response: any) => {
                const held = response?.data?.PlatformMessage?.ListOfObjectRelationship?.ObjectRelationship;
                const edges: any[] = held == null ? [] : (Array.isArray(held) ? held : [held]);
                if (live) setFetchedCustomerEdge(edges.find(one => one?.ObjectRelationshipTypeName === "Project Customer") ?? null);
            })
            .catch(() => { if (live) setFetchedCustomerEdge(null); });
        return () => { live = false; };
    }, [api, isProject, record?.ObjectUSID]);
    const customerEdge = useMemo(() => {
        const held = record?.ListOfObjectRelationship?.ObjectRelationship;
        const edges: any[] = held == null ? [] : (Array.isArray(held) ? held : [held]);
        const wanted = isProject ? "Project Customer" : "Contract Customer";
        return edges.find(one => one?.ObjectRelationshipTypeName === wanted) ?? fetchedCustomerEdge ?? null;
    }, [record, isProject, fetchedCustomerEdge]);
    // THE FOLDER'S CONTENTS AS TABS, RIGHT AFTER DETAIL. Ed, 2026-10-27: the five kinds and the
    // Timeline were a second row under the strip; they are the strip now, which is also why the
    // strip learned to fold its tail into a menu when the window is narrow.
    const projectTabs: string[] = isProject ? [...MEMBER_KINDS, "Timeline"] : [];
    // THE CUSTOMER AS THE PROJECT CAPTURED IT -- company, contact and address -- from the project's
    // own door, which puts the three on every project row (Ed, 2026-10-29: "capture Customer
    // Company, Customer Name and Customer Email when creating a new Project"). Read beside the
    // record rather than off it because the species document carries neither the edge nor the
    // contact; one small read, keyed on the revision on screen.
    const [projectCustomer, setProjectCustomer] = useState<{company?: string | null; objectId?: string | null;
                                                            objectUSID?: string | null; name?: string | null;
                                                            email?: string | null} | null>(null);
    useEffect(() => {
        if (!isProject || !usid) { setProjectCustomer(null); return; }
        let live = true;
        api.get("t1/v1/projects", {params: {USID: usid}, headers: {"idoc-response": "v1.ProjectRevision-001"}})
            .then((response: any) => {
                const held = response?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision;
                const rows: any[] = held == null ? [] : (Array.isArray(held) ? held : [held]);
                const row = rows[0] ?? response?.data?.PlatformMessage?.ProjectRevision;
                if (live) setProjectCustomer(row ? {company: row.CustomerCompany, objectId: row.CustomerObjectID,
                                                    objectUSID: row.CustomerObjectUSID, name: row.CustomerName,
                                                    email: row.CustomerEmail} : null);
            })
            .catch(() => { if (live) setProjectCustomer(null); });
        return () => { live = false; };
    }, [api, isProject, usid, reloadTick]);
    // THE RAIL'S TREE UNDER EVERY PROJECT. Ed, 2026-10-29: "Show Products, Contracts, Quote
    // Requests, Quote Responses, Build Plans" -- his order, which is not the strip's, so it is
    // written out rather than derived from MEMBER_KINDS -- and then "Each project in the list
    // should show" them, not only the open one.
    const projectChildren = useMemo(() => (["Product", "Contract", "QuoteRequest", "PreparedQuote", "BuildPlan"] as const)
        .map(kind => ({key: kind, label: KIND_LABEL[kind], icon: KIND_ICON[kind]})), []);
    // THE FACTS BEHIND THE TREE, for a page of project rows at once. The customer pill is a
    // 'Project Customer' edge, not a column, and the flat list document does not carry edges --
    // so one relationship read for every listed project (ParentUSID IN ...), filtered to the
    // customer edge on this side because the type name is a joined column the query cannot be
    // asked about. The counts are the membership read's, one per project, asked together.
    const projectFacts = useCallback(async (rows: {usid: string; objectUSID: string | null}[]) => {
        const parents = rows.map(one => one.objectUSID).filter(Boolean) as string[];
        const out: Record<string, {pill?: string | null; counts?: Record<string, number>}> = {};
        if (parents.length === 0) return out;
        const qbe = btoa(JSON.stringify({PlatformMessage: {"-idoc": "v1.ObjectRelationship-001", ObjectRelationship: {
            _attributes: {limit: 500, offset: 0},
            QueryGroups: {QueryGroup: [{Name: "primary", Operator: "AND", Type: "QueryGroup", Membership: {Member: [
                {Type: "Attribute", Name: "ParentUSID", Value: parents.join(","), Operator: "IN"},
            ]}}]},
        }}}));
        const [edgesResponse, ...memberResponses] = await Promise.all([
            api.get("t1/v1/object_relationships",
                    {params: {QBE: qbe}, headers: {"idoc-response": "v1.ObjectRelationship-001"}}).catch(() => null),
            ...parents.map(parent => api.get("t1/v1/project_members",
                                             {params: {USID: parent}, headers: {"idoc-response": "v1.ProjectMember-001"}})
                                        .then((response: any) => [parent, response?.data?.PlatformMessage?.Counts ?? {}])
                                        .catch(() => [parent, undefined])),
        ]);
        const held = edgesResponse?.data?.PlatformMessage?.ListOfObjectRelationship?.ObjectRelationship;
        const edges: any[] = held == null ? [] : (Array.isArray(held) ? held : [held]);
        for (const edge of edges) {
            if (edge?.ObjectRelationshipTypeName === "Project Customer" && edge.ParentUSID && edge.ChildName) {
                out[String(edge.ParentUSID)] = {...out[String(edge.ParentUSID)], pill: String(edge.ChildName)};
            }
        }
        for (const [parent, counts] of memberResponses as [string, Record<string, number> | undefined][]) {
            if (counts) out[parent] = {...out[parent], counts};
        }
        return out;
    }, [api]);
    // A PROJECT'S FIRST TAB IS "Overview", NOT "Detail". Ed, 2026-10-29: "change the Detail tab to
    // Overview and it must show this" -- the source product's Wizard, which is our pipeline board.
    // The pane KEY stays "Detail" so every `pane === "Detail"` fallback on this page still lands.
    const labelFor = (p: string) => (MEMBER_KINDS as readonly string[]).includes(p)
        ? `${KIND_LABEL[p]} (${memberCounts[p] ?? 0})` : (p === "Detail" && isProject ? "Overview" : p);

    // ── THE FIVE THINGS FILED IN A PROJECT HAVE NO ACCESS OF THEIR OWN ──────────────────────
    //
    // Ed's rule of 2026-10-27: forms and audit trails, no independent lifecycle, no visibility
    // definitions and no permissions tab. The folder is the authority -- `project:*` gates every
    // read and write inside it -- so both panes are withheld rather than drawn empty.
    //
    // AND DRAWING THEM WOULD NOW BE THE EMPTY-BLOCKS FAILURE EXACTLY. The Permissions grid builds
    // each row as `${singular}: ${verb}` and resolves it against raw.PRIVILEGE.NAME; those 60
    // privilege names are retired, so a Contract would draw fifteen headings that can never match
    // anything, with no error anywhere -- which is what Ed found on the project page this morning,
    // arrived at from the other direction.
    //
    // VISIBILITY GOES FOR THE SAME REASON: a filed record takes the folder's owning position, so a
    // share pane on the record would offer to re-own something whose owner is decided by where it
    // is filed.
    const filedInProject = !!effective.filedInProject;
    // THE FILES ARE NOT A TAB (Ed, 2026-10-31: "transfer the functionality from the attachment tab
    // ... to the area below the detail pane. Remove the attachment pane afterward."): they are drawn
    // under the record's own card on Detail, where a reader meets them without choosing a tab. The
    // two content tabs that remain, the form and the authored text, follow the record's own panes.
    // THE PANES THAT ARE *ABOUT* THE RECORD, with the two access ones conditional. BASE_PANES
    // holds Detail, Visibility, Approvals, Permissions, History; `slice(1)` used to append the last
    // four unconditionally.
    const aboutPanes = [...(filedInProject ? [] : ["Visibility"]), "Approvals",
                        ...(filedInProject ? [] : ["Permissions"]), "History"];
    // NEITHER RELATIONSHIP TAB ON A PROJECT, on Ed's rule of 2026-10-27: a folder's relationships
    // are its plumbing -- the only edge it owns is `Project Customer`, which the Customer field
    // states in words -- and what is IN the folder is a membership, not an edge, shown by the
    // Contents tabs. Scoped to Project and not to `filedInProject`: the things filed in a folder
    // keep both, because a contract's edges to an item, a supplier or a customer are its own
    // subject matter.
    const relationPanes = effective.code !== "Project" ? [RELATED_PANE, RELATED_TO_PANE] : [];

    const panes = hasForm ? [BASE_PANES[0], ...projectTabs, ...requestPane, ...quotePane, ...planPanes, ...qualityPane, ...questionnairePane, ...contractPane, ...inventoryPane, ...receiptPane, ...itemSuppliersPane, ...orderReceiptsPane, ...supplierPane, ...developmentPane, FORM_PANE, PARKOUR_PANE, ...sbomPane, ...customersPane, ...quizPane, ...relationPanes, ...aboutPanes]
                          : [BASE_PANES[0], ...projectTabs, ...requestPane, ...quotePane, ...planPanes, ...qualityPane, ...questionnairePane, ...contractPane, ...inventoryPane, ...receiptPane, ...itemSuppliersPane, ...orderReceiptsPane, ...supplierPane, ...developmentPane, PARKOUR_PANE, ...sbomPane, ...customersPane, ...quizPane, ...relationPanes, ...aboutPanes];


    // A SELECTED PANE THAT IS NO LONGER OFFERED FALLS BACK TO Detail.
    //
    // THE PANE LIST IS CONDITIONAL AND THE SELECTION OUTLIVED IT. Moving between records in the
    // sidebar keeps this component mounted, so `pane` survives while `panes` is rebuilt for the
    // new record -- and a tab that has gone from the strip was still rendering its body
    // underneath. Found by Ed on 2026-10-19: from a software item with a bill of materials to a
    // cannulated screw without one, the SBOM tab correctly disappeared and the SBOM pane kept
    // drawing, stating that a composition existed for a record that has none.
    //
    // NOT AN SBOM PROBLEM, though that is what exposed it: Form and the quiz are conditional on
    // the same kind of per-record state and had the same latent fault. Fixed once, here, over the
    // list itself rather than per pane.
    useEffect(() => {
        // THE RECORD IT WAS CHOSEN FOR, AND NO OTHER: the old record is still on screen while the
        // new one loads, and its pane list would accept the choice for the wrong page.
        if (pendingPane.current && record?.USID === pendingPane.current.usid && panes.includes(pendingPane.current.pane)) {
            setPane(pendingPane.current.pane);
            pendingPane.current = null;
            return;
        }
        if (panes.length && !panes.includes(pane)) {
            setPane("Detail");
        }
        // KEYED ON THE LIST'S CONTENT, not its identity: `panes` is rebuilt every render, so the
        // array itself is never equal to the last one and this would run on every pass. And on
        // the record's identity, for the pending choice above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panes.join("|"), pane, record?.USID]);

    // One request for every block on the Permissions tab, not one per block. The privilege names are
    // per classification, so this re-resolves if the loaded record turns out to be another kind.
    // WHAT THE RAIL LISTS. Every Foundation classification is ObjectRevision rows discriminated by
    // a column, so the rail has to be told which kind it is beside -- unscoped it would list the
    // whole estate next to a Document. Keyed on the EFFECTIVE classification, which is the record's
    // own: follow a relationship to a Contract and the rail becomes a list of Contracts.
    const scopeToClassification = useCallback((ec: any) => {
        ec.setClassificationFilter(effective.code);
        // NO IDOC IS SET ON PURPOSE -- the component derives it from the classification above
        // (v1.DocumentRevision-001 for a Document), and the envelope's -idoc must match the payload
        // node it writes. Naming it here overrode that and broke the query outright.
        //
        // A SORT IS NOT OPTIONAL FOR A PAGED LIST. Without one the server is free to return rows in
        // any order, and "page 2" of an unordered result can repeat or skip what page 1 held -- a
        // rail that loads as you scroll would quietly show duplicates. Same order as the list.
        // SortAttribute, not Name -- getFormattedSortSpecification reads that exact key and any
        // other spells the sort "undefined:DESC", which the service rejects.
        ec.setSortSpecification([{SortAttribute: "CreatedDatetime", DisplayName: "Created",
                                  Direction: "DESC"}]);
    }, [effective.code]);

    const objectPrivileges = useMemo(() => objectPrivilegeNames(effective), [effective]);
    const {rolesByPrivilege, loaded: rolesLoaded, failed: rolesFailed} = usePrivilegeRoles(objectPrivileges);

    // Relationship and audit grids are EC-backed. Built once and keyed on the LOGICAL object rather
    // than the revision, because both follow the object across its revisions.
    // The relationships the detail document returned WITH the record, so the tab does not have to ask
    // for them again. Null until the record loads; [] is a real answer meaning "this record has none".
    const [nestedRelationships, setNestedRelationships] = useState<any[] | null>(null);

    const relationshipEO: EnterpriseObject | undefined = useMemo(() => enterpriseObjectClassFactory("ObjectRelationship"), []);
    // A SECOND COMPONENT, NOT THE SAME ONE FILTERED TWICE. Both grids read ObjectRelationship, but
    // one is scoped by ParentUSID and the other by ChildUSID -- a single component can hold one
    // scope, and sharing it would make each grid's query overwrite the other's on every refresh.
    const relatedToEO: EnterpriseObject | undefined = useMemo(() => enterpriseObjectClassFactory("ObjectRelationship"), []);
    const auditEO: EnterpriseObject | undefined = useMemo(() => enterpriseObjectClassFactory("ObjectAuditHistory"), []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                // THE DETAIL DOCUMENT, not the flat one. It returns this revision with its logical
                // object, that object's type, its relationships and its files in a single call --
                // which is the whole reason it exists. The UNSCOPED -002 rather than the
                // classification's: the route carries a revision USID and this page is reached with
                // links from other classifications, so naming PurchaseOrderRevision-002 for a
                // document would return nothing and read as "not found".
                //
                // History is deliberately NOT in it. An audit trail grows without bound while the
                // record does not, so it keeps its own fetch on the tab that shows it.
                const response = await api.get("t1/v1/object_revisions", {
                    params: {USID: usid},
                    headers: {"idoc-response": "v1.ObjectRevision-002"},
                });
                const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
                const rows = Array.isArray(list) ? list : [list];
                if (cancelled) return;

                if (rows.length === 0) {
                    setError(`That ${classification.singular.toLowerCase()} could not be found.`);
                    setRecord(null);
                    setNestedRelationships(null);
                } else {
                    setRecord(rows[0]);
                    // A group with one member arrives as an object rather than an array, which is
                    // how the platform serialises every list -- normalise before anything counts it.
                    const nested = rows[0]?.ListOfObjectRelationship?.ObjectRelationship;
                    setNestedRelationships(nested == null ? [] : (Array.isArray(nested) ? nested : [nested]));
                }
                setLoading(false);
            } catch (e) {
                console.error("failed to load object", e);
                if (!cancelled) {
                    setError(`We could not load this ${classification.singular.toLowerCase()}. Please try again.`);
                    setLoading(false);
                }
            }
        };

        if (usid) load();

        return () => {
            cancelled = true;
        };
        // `classification.singular` belongs here: the effect reads it to word its error messages.
        // A string off a prop that changes only on navigating to a different object type, at which
        // point `usid` changes too, so this adds no extra loads.
    }, [api, usid, reloadTick, classification.singular]);

    // Point the child grids at the loaded record. Both key on the LOGICAL object so they survive a
    // revise, and both re-query only when that object changes.
    //
    // SCOPED IN THE RENDER PASS, BEFORE THE GRIDS MOUNT -- and this is the fix, not a tidy-up.
    //
    // PlatformTable queries itself on mount. The effect below runs AFTER that mount, so every grid
    // here issued one UNSCOPED query first and drew every relationship in the realm: 183 rows on a
    // record with none, "is a component of Power Supply Component" on a document that is connected
    // to nothing. No error anywhere, because an empty query is a legal query.
    //
    // Gating the mount on record?.ObjectUSID was not enough -- it delays the mount until the USID is
    // known, but the effect still runs after it. A useMemo runs DURING render, so the scope is on the
    // component before any child of this render exists to query. ObjectList makes the same move for
    // the same reason, in the useMemo that builds its enterprise object.
    //
    // The effect below still seeds and fetches; what changed is that nothing can now query unscoped
    // while it waits its turn.
    useMemo(() => {
        const objectUSID = record?.ObjectUSID;
        if (!objectUSID) return;
        relationshipEO?.getEnterpriseComponent("ObjectRelationship")?.setScope?.("ParentUSID", objectUSID);
        relatedToEO?.getEnterpriseComponent("ObjectRelationship")?.setScope?.("ChildUSID", objectUSID);
        auditEO?.getEnterpriseComponent("ObjectAuditHistory")?.setScope?.("ObjectUSID", objectUSID);
    }, [record?.ObjectUSID, relationshipEO, relatedToEO, auditEO]);

    // THROUGH scopeGridTo, WHICH EXISTS BECAUSE THE ORDER HERE IS EASY TO GET SILENTLY WRONG. This
    // used to end with `invalidate()`, reading as "configure, then refresh" -- but invalidate throws
    // the query buffer away and then synchronously drives PlatformTable into an unfiltered
    // executeQuery. Both grids showed every row in the realm, with no error anywhere, because an
    // empty query is a legal query. See scopeGrid.ts.
    useEffect(() => {
        const objectUSID = record?.ObjectUSID;
        if (!objectUSID) return;

        // RELATIONSHIPS CAME WITH THE RECORD. Seeding sets the same scope scopeGridTo would and
        // fills page 1 from what the detail document already returned, so opening the tab costs no
        // request. A sort, a search or a refresh re-queries the server exactly as before -- the
        // scope is what survives, so the fallback is a redundant fetch and never the wrong rows.
        //
        // If the document returned nothing for them -- an older payload, a shape that changed --
        // nestedRelationships is null rather than [], and the grid fetches for itself as it always did.
        // AN EMPTY SEED IS NOT A SEED. nestedRelationships arrives as [] for a record with no
        // relationships, which is !== null, so the seeding branch was taken and scopeGridTo never
        // ran for ParentUSID at all -- leaving the unscoped mount query's 183 rows standing as the
        // grid's final state. Seeding is now only claimed when there is something to seed WITH.
        const seeded = Array.isArray(nestedRelationships) && nestedRelationships.length > 0
            && seedGridWith(relationshipEO?.getEnterpriseComponent("ObjectRelationship"),
                            "ParentUSID", objectUSID, nestedRelationships);
        if (!seeded) {
            scopeGridTo(relationshipEO?.getEnterpriseComponent("ObjectRelationship"),
                        "ParentUSID", objectUSID);
        }

        // The reverse view. No seeding: the detail document carries the edges this record OWNS, not
        // the ones pointing at it, so this one always asks.
        scopeGridTo(relatedToEO?.getEnterpriseComponent("ObjectRelationship"),
                    "ChildUSID", objectUSID);

        // HISTORY IS NOT IN THE DETAIL DOCUMENT and must not be: it grows without bound while the
        // record it describes does not, so every read of a record would pay for its whole history.
        scopeGridTo(auditEO?.getEnterpriseComponent("ObjectAuditHistory"),
                    "ObjectUSID", objectUSID);
    }, [record?.ObjectUSID, nestedRelationships, relationshipEO, auditEO, relatedToEO]);

    // RE-READ THE HISTORY EACH TIME IT IS OPENED. The effect above runs when the RECORD changes, and
    // editing a form does not change the record this page is holding -- the answers are written by
    // t5 and the audit row with them. So the grid kept whatever it had loaded when the page opened,
    // which for a record somebody was about to edit was nothing, and the tab said the change had not
    // been recorded when it had.
    //
    // Keyed on the pane rather than on the save, because history is written by more than the form:
    // releasing, revising and sharing all add rows, and each would otherwise need to remember to
    // refresh this. Opening the tab is the one moment that covers all of them.
    useEffect(() => {
        if (pane !== "History") return;
        const objectUSID = record?.ObjectUSID;
        if (!objectUSID) return;

        scopeGridTo(auditEO?.getEnterpriseComponent("ObjectAuditHistory"),
                    "ObjectUSID", objectUSID);
    }, [pane, record?.ObjectUSID, auditEO]);

    /** The USID of the object's draft revision, or null when it has none. */
    const findDraftFor = async (objectUSID?: string): Promise<string | null> => {
        if (!objectUSID) return null;
        try {
            const response = await api.get("t1/v1/object_revisions", {
                params: {ObjectUSID: objectUSID, Status: "DRAFT", Limit: 1},
                headers: {"idoc-response": "v1.ObjectRevision-001"},
            });
            const rows = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision;
            const row = Array.isArray(rows) ? rows[0] : rows;
            return row?.USID ?? null;
        } catch (e) {
            console.error("could not find the draft raised by Revise", e);
            return null;
        }
    };

    // WHETHER A DRAFT IS OPEN decides which moves this revision may still make, so it is asked on
    // every load rather than only after a Revise. Re-run on reloadTick so cancelling or releasing
    // the draft elsewhere gives this page its buttons back without a refresh.
    useEffect(() => {
        let cancelled = false;
        const objectUSID = record?.ObjectUSID;
        if (!objectUSID) { setDraftUSID(null); return; }
        findDraftFor(objectUSID).then(draft => { if (!cancelled) setDraftUSID(draft); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record?.ObjectUSID, record?.Status, reloadTick]);

    const setTrainingRequired = async (value: boolean) => {
        setSavingTraining(true);
        try {
            await saveTrainingSetup(sessionContext, usid!, {TrainingRequired: value});
            // THE WHOLE RECORD IS RE-READ rather than the flag patched locally: the Quizzes tab appears
            // and disappears on this value, and a tab list drawn from optimistic state would be a tab
            // that exists until the next reload disagrees.
            setReloadTick(t => t + 1);
        } finally {
            setSavingTraining(false);
        }
    };

    /**
     * Open the edit dialog, seeded from the record as it stands.
     *
     * SEEDED ON OPEN AND NOT HELD IN STATE, so a cancelled edit cannot reappear pre-filled the
     * next time somebody opens it -- the same reason the create dialog resets between openings.
     */
    const startEditing = () => {
        setDraftTitle(record?.Name ?? "");
        setDraftDescription(record?.Description ?? "");
        setEditRefusal(null);
        setEditing(true);
    };

    /**
     * Write the record's own fields.
     *
     * NAME AND DESCRIPTION, AND DELIBERATELY NOT THE IDENTIFIER. A customer-numbered type takes
     * its Object ID from whoever created it, so it looks editable in the same way -- but an
     * identifier somebody has already quoted must never come to name a second thing, which is the
     * rule the change-order numbering enforces at the other end. Everything else on the Detail
     * pane is derived or lifecycle: the revision, the status, the dates, the form it was seeded
     * from, the approval. None of those is a field anybody should type into.
     *
     * THE SERVER DECIDES WHETHER IT MAY BE WRITTEN. The button is hidden outside a DRAFT and
     * without the edit privilege, which keeps the page from offering a refusal -- but
     * `pre_write_record_event_object_revision` enforces the draft rule and object_privilege_guard
     * the privilege, so hiding the control is the courtesy and the guard is the rule.
     */
    const saveEdit = async () => {
        const name = draftTitle.trim();
        if (!name) {
            setEditRefusal("A record needs a name.");
            return;
        }
        setSavingEdit(true);
        setEditRefusal(null);
        try {
            await api.put("t1/v1/object_revisions", {
                PlatformMessage: {
                    "-idoc": "v1.ObjectRevision-001",
                    ObjectRevision: {USID: usid, Name: name, Description: draftDescription},
                },
            }, {headers: {"idoc-request": "v1.ObjectRevision-001",
                          "idoc-response": "v1.ObjectRevision-001"}});
            setEditing(false);
            // THE WHOLE RECORD IS RE-READ rather than patched locally: the rail beside the page
            // lists this record by name, and optimistic state would leave the two disagreeing
            // until something else reloaded.
            setReloadTick(t => t + 1);
        } catch (problem: any) {
            // The backend owns the draft rule and the privilege, so surface its reason rather
            // than inventing one.
            setEditRefusal(problem?.response?.data?.Cause
                           ?? problem?.response?.data?.Message
                           ?? "That change could not be saved.");
        } finally {
            setSavingEdit(false);
        }
    };

    /** Close or reopen a project: the only two moves it has. PUT projects {USID, Status}. */
    const setProjectState = async (next: "OPEN" | "CLOSED") => {
        if (!usid) return;
        setBusyAction(next);
        setError(null);
        try {
            await api.put("t1/v1/projects", {USID: usid, Status: next});
            setReloadTick(t => t + 1);
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause
                     ?? (next === "CLOSED" ? "The project could not be closed." : "The project could not be reopened."));
        } finally {
            setBusyAction(null);
        }
    };

    const performLifecycleAction = async (action: string, extra?: Record<string, any>) => {
        // UNSAVED WORK IS ASKED ABOUT FIRST, BEFORE ANY OTHER QUESTION AND FOR EVERY ACTION.
        //
        // The lifecycle buttons sit at the top of the record and the editing happens in a tab below
        // them, with nothing connecting the two: pressing Release over a half-filled panel released
        // what the SERVER held and discarded the edits without a word. Ed did that on Training
        // Curricula 1 -- configured it, released without pressing Save -- and the first symptom was
        // Carmen Solis reporting training she had never been assigned.
        //
        // EVERY ACTION, not just Release. Revise and Submit for Approval throw the same work away,
        // and Revise is worse: it opens a new draft, so the edits are lost against a revision that
        // no longer exists.
        if (!extra?.__unsavedAcknowledged) {
            const unsaved = pendingUnsavedChanges();
            if (unsaved.length > 0) {
                setUnsavedWarning({action, extra: extra ?? {}, tabs: unsaved});
                return;
            }
        }

        // RELEASING ASKS UP TO TWO QUESTIONS, in this order, and each one adds to the same payload.
        // The "__" keys below are control flow, not data, and are stripped before the request: they
        // record that a question has been PUT, which is not the same as it having an answer -- somebody
        // who releases with no effective date has answered "immediately", and asking them twice
        // because the key is absent would be a loop.
        if (action === "Release") {
            // WHEN DOES IT GOVERN? Released now, or in force from a date -- the gap between the two
            // is the window people train in.
            if (!extra?.__effectiveAsked) {
                setAskingEffective(true);
                return;
            }
            // AND DOES THE CHANGE RETRAIN? The server refuses the release without an answer
            // (CC-OBRV-RTRN) precisely so no path can skip it; asking here means the person meets
            // that as a question rather than as a refusal.
            //
            // UNLESS IT IS ALREADY ANSWERED. The question is now asked at SUBMIT, of the author --
            // under an approval matrix release happens on the final signature, so asking it here
            // put it to an approver with nowhere to answer it. A stored answer satisfies the server,
            // so re-asking here would be asking somebody to re-decide something already signed for.
            // This branch remains for the direct release, where the releaser IS the author.
            if (record?.TrainingRequireRetraining === true
                    // ?? null, NOT === null: an unanswered column can arrive as null OR as an
                    // absent key, and only `false` must count as answered. `!record?.X` would
                    // re-ask everybody who answered No.
                    && (record?.TrainingResetRequired ?? null) === null
                    && !("TrainingResetRequired" in extra)) {
                setPendingRelease(extra);
                setAskingRetrain(true);
                return;
            }
        }

        setBusyAction(action);
        try {
            await api.put("t1/v1/object_revisions", {
                PlatformMessage: {
                    "-idoc": "v1.ObjectRevision-001",
                    ObjectRevision: {USID: usid, LifecycleAction: action,
                                     ...Object.fromEntries(Object.entries(extra ?? {})
                                                                 .filter(([key]) => !key.startsWith("__")))},
                },
            }, {headers: {"idoc-request": "v1.ObjectRevision-001", "idoc-response": "v1.ObjectRevision-001"}});

            // REVISE OPENS THE DRAFT IT JUST MADE. Staying here left the reader on the revision
            // they had revised -- still RELEASED, unchanged, and offering Revise again. The draft
            // was real but on no screen they could reach, so the natural second click asked for a
            // second draft and was refused. Reported by Ed against PO-00137: revised once, then
            // "we could not revise this purchase order" and no sign of the revision he had made.
            //
            // The new draft is found rather than returned: the response carries the revision that
            // was written, which is the OLD one -- Revise leaves it exactly as it was and inserts
            // the draft beside it. The object has at most one draft at a time (the server refuses a
            // second), so asking for it by object and status is unambiguous.
            if (action === "Revise") {
                const draft = await findDraftFor(record?.ObjectUSID);
                if (draft) {
                    navigate(`${effective.route}/${draft}`);
                    return;
                }
                // Fall through to a reload: the draft exists (the server said so) but could not be
                // read back, and leaving the page untouched would be the worse of the two answers.
            }

            // A transition can change more than this row -- publishing supersedes the revision it
            // replaces. Reload rather than patching state locally, which would leave a stale status
            // on screen.
            setReloadTick(t => t + 1);
        } catch (e: any) {
            console.error("lifecycle action failed", e);
            // The server owns these rules, so surface its reason rather than inventing one.
            const message = e?.response?.data?.Cause || e?.response?.data?.Message;
            setError(message || `We could not ${action.toLowerCase()} this ${classification.singular.toLowerCase()}.`);
        } finally {
            setBusyAction(null);
        }
    };

    const Icon = effective.icon;

    const status = record?.Status ?? "";
    // RELEASED but not what the record resolves to, with a date still ahead: the gap
    // between official and governing. IsCurrent alone is not enough -- a draft is not
    // current either -- so the status has to say RELEASED as well.
    const notYetInForce = status === "RELEASED" && record?.IsCurrent === false
        && !!record?.EffectiveDatetime && new Date(record.EffectiveDatetime) > new Date();

    return (
        // height 100%, NOT JUST flexGrow. The shell hands this page a fixed box that scrolls, and a
        // block parent lets a flex child grow past it -- so the page stretched to 26,000px and the
        // rail, whose whole design is to scroll inside its own column, grew with it. Its divider
        // then painted to the height of the CONTENT rather than the window. A definite height makes
        // the flexGrow/minHeight chain below mean something: the rail and the detail each scroll in
        // their own pane, and the divider spans the full height because its row now has one.
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, height: "100%",
                     backgroundColor: White, minHeight: 0}}>
            <PageHeader title={effective.plural}>
                <PersonaHeaderInfo/>
            </PageHeader>

            {/* THE LIST YOU CAME FROM, KEPT BESIDE THE RECORD YOU OPENED. Scoped to this page's
                classification, because every Foundation classification is ObjectRevision rows and a
                rail beside a Document that listed Contracts too would be worse than no rail. */}
            <div style={{display: "flex", flexDirection: "row", flexGrow: 1, minHeight: 0}}>
                {/* ON A PROJECT THE RAIL IS A TREE: each project row carries its number, object
                    id, customer pill and name, and the open one unfolds into what it holds, a row
                    per kind with its count (Ed, 2026-10-29, from the source product's pane). The
                    rows open the same panes the strip does. Every other classification gets the
                    plain list. */}
                <RecordRail
                    componentName="ObjectRevision"
                    currentUSID={usid}
                    routeFor={(one: string) => `${effective.route}/${one}`}
                    navigate={navigate}
                    configure={scopeToClassification}
                    defaultStatuses={defaultStatusesFor(effective.code)}
                    title={effective.plural}
                    facts={isProject ? projectFacts : undefined}
                    children={isProject ? projectChildren : undefined}
                    currentCounts={memberCounts}
                    childSelected={pane}
                    onChildSelect={(entry, key) => {
                        // ON THE OPEN PROJECT, open the pane; on another, go there and open it
                        // once it has loaded -- see pendingPane.
                        if (entry.isCurrent) { setPane(key); return; }
                        pendingPane.current = {usid: entry.usid, pane: key};
                        navigate(`${effective.route}/${entry.usid}`);
                    }}
                />

            <div style={{flex: 1, overflow: "auto", padding: "0 30px", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0}}>
                {loading && (
                    <div style={{display: "flex", alignItems: "center", gap: 12, marginTop: 24}}>
                        <CircularProgress size={20}/>
                        <Typography style={{fontSize: 14, color: Grey7}}>Loading…</Typography>
                    </div>
                )}

                {!loading && error && !record && (
                    <Typography style={{fontSize: 14, color: "#B3261E", marginTop: 24}}>{error}</Typography>
                )}

                {record && (
                    <>
                        {/* Header: name, status pill, lifecycle actions */}
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #E4E7EC", gap: 20}}>
                            <div style={{display: "flex", alignItems: "center", gap: 12, minWidth: 0}}>
                                {/* The breadcrumb used to be the only way back to the list, so removing
                                    it without this would strand anyone who opened a document. */}
                                <Tooltip title={`Back to ${effective.plural}`}>
                                    <IconButton disableRipple onClick={() => navigate(effective.route)} sx={{ml: "-8px"}}>
                                        <ArrowBackOutlined sx={{color: Grey6}}/>
                                    </IconButton>
                                </Tooltip>
                                <Box sx={{width: 34, height: 34, borderRadius: "6px", backgroundColor: "rgba(20,152,216,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
                                    <Icon sx={{color: Blue3, fontSize: 20}}/>
                                </Box>
                                <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                    {record.ObjectID ? `${record.ObjectID} — ${record.Name || "Untitled"}` : (record.Name || "Untitled")}
                                </Typography>
                                {status && statusPill(status)}
                                {/* NO "Rev n" ON A PROJECT: a project is OPEN or CLOSED and has no
                                    lifecycle, so its revision is a fact of the storage and not of the
                                    record (Ed, 2026-10-28). Every other species keeps it. */}
                                {record.DisplayRevision && effective.code !== "Project" && (
                                    <Typography style={{fontSize: 13, color: Grey7, flexShrink: 0}}>Rev {record.DisplayRevision}</Typography>
                                )}
                            </div>
                            <div style={{display: "flex", gap: 10, flexShrink: 0, alignItems: "center"}}>
                                {/* SAYING WHY THE BUTTONS ARE GONE, and where to go instead. Removing
                                    Revise and Deprecate without a word would read as the page being
                                    broken -- the reader knows those moves exist and cannot see why
                                    this record is refusing them. */}
                                {draftUSID && draftUSID !== usid && (
                                    <Typography style={{fontSize: 13, color: Grey7}}>
                                        A draft is in progress —{" "}
                                        <span onClick={() => navigate(`${effective.route}/${draftUSID}`)}
                                              style={{color: Blue3, cursor: "pointer", textDecoration: "underline"}}>
                                            open it
                                        </span>
                                    </Typography>
                                )}
                                {/* PUBLISHED, NOT YET IN FORCE. A released revision that is not the
                                    one the record resolves to is not an error and not a draft --
                                    it is the window between being official and governing, and
                                    without saying so the page shows a RELEASED record while the
                                    app keeps answering with the previous revision, which reads as
                                    a bug. Only ever visible during that window. */}
                                {notYetInForce && (
                                    <Typography style={{fontSize: 13, color: "#B54708",
                                                        backgroundColor: "#FFFAEB",
                                                        border: "1px solid #FEDF89", borderRadius: 8,
                                                        padding: "8px 12px"}}>
                                        Released, and <b>in force from {formatLocal(record.EffectiveDatetime)}</b>.
                                        Until then the previous revision still applies — this is the
                                        window for people to be trained on it.
                                    </Typography>
                                )}
                                {/* EDIT, AND ONLY ON A DRAFT. The server refuses a field edit
                                    outside one, so a button offered on a RELEASED record would be
                                    a refusal waiting to happen -- the same reason the Form pane
                                    goes readOnly rather than saving and failing.

                                    AND ONLY WITH THE PRIVILEGE. Hiding an action somebody may not
                                    take is the rule this estate already follows for every other
                                    control; the guard on the write is what enforces it. */}
                                {String(status ?? "").toUpperCase() === "DRAFT"
                                    && hasPrivilege(objectPrivilegeCode(effective, "edit")) && (
                                    // THE SAME BOX AS A LIFECYCLE BUTTON, down to the Tooltip and
                                    // the span inside it. They sit in one row, so anything this
                                    // one does differently -- its own font size, its own border,
                                    // no wrapper -- reads as a button that does not belong. Styled
                                    // from LifecycleActions rather than approximately like it,
                                    // because "approximately" is how a row comes to be a pixel out.
                                    //
                                    // AND THE TITLE IS SHORT, WHICH IS LOAD-BEARING. A Tooltip
                                    // renders its title as an aria-label on the span it wraps, so
                                    // "Change the name and description of this draft" put a second
                                    // element labelled "Name" -- and one labelled "Description" --
                                    // onto every record page. It broke the quoting spec, which had
                                    // been addressing its own Description field by label for weeks
                                    // and suddenly matched two things. A tooltip repeating a field
                                    // name anywhere on the page is a collision waiting for whoever
                                    // writes the next test.
                                    <Tooltip title={busyAction
                                        ? `${busyAction} in progress…`
                                        : "Edit this draft"}>
                                        <span>
                                            <Button variant="outlined" size="small"
                                                    disabled={!!busyAction}
                                                    startIcon={<EditOutlined sx={{fontSize: 18}}/>}
                                                    onClick={startEditing}
                                                    sx={{textTransform: "none", fontSize: 13,
                                                         borderColor: "#A8A9AF", color: Grey17,
                                                         "&:hover": {borderColor: Blue3,
                                                                     color: Blue3,
                                                                     backgroundColor: "transparent"}}}>
                                                Edit
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                                {/* NO LIFECYCLE BUTTONS ON A THING FILED IN A PROJECT.
                                    Ed's rule of 2026-10-27: the five ingredients have forms and
                                    audit trails and NO independent lifecycle -- the folder is what
                                    gets released and signed, so Release, Revise, Deprecate and
                                    Cancel belong on the project and not on a contract inside it.

                                    THE ENGINE'S TRANSITIONS STILL EXIST, and that is deliberate:
                                    this withholds the BUTTONS, not the machinery. Their revisions
                                    therefore stay DRAFT, which is why the server now freezes them
                                    from the FOLDER's state instead -- see species_gate's
                                    require_folder_open, without which every DRAFT-only protection
                                    these records had would have quietly stopped applying. */}
                                {/* A PROJECT IS OPEN OR CLOSED, and nothing else. Ed, 2026-10-27,
                                    evening, correcting the morning: the five things filed in a
                                    project HAVE lifecycles of their own, and the project has none
                                    beyond open and closed. So the five get the standard buttons
                                    back, and a project gets these two instead of them. */}
                                {isProject ? (
                                    <Button size="small" variant="outlined" disabled={Boolean(busyAction)}
                                            sx={{textTransform: "none"}}
                                            onClick={() => setProjectState(
                                                String(status).toUpperCase() === "CLOSED" ? "OPEN" : "CLOSED")}>
                                        {String(status).toUpperCase() === "CLOSED" ? "Reopen project" : "Close project"}
                                    </Button>
                                ) : (
                                    <LifecycleActions status={status}
                                                      actions={availableActions}
                                                      busyAction={busyAction}
                                                      onAction={performLifecycleAction}/>
                                )}
                            </div>
                        </div>

                        {/* UNDER A PROJECT'S NAME: who it is for, their contact, and what it is --
                            the three lines the source product puts under the title (Ed's screenshot,
                            2026-10-29). They sit above the strip so they hold on every pane; the
                            Overview below them is the pipeline, not a card of fields. */}
                        {isProject && (projectCustomer?.company || customerEdge || record.Description) && (
                            <div style={{padding: "8px 0 0 46px", display: "flex", flexDirection: "column", gap: 2}}>
                                {(projectCustomer?.company || customerEdge) && (
                                    <Typography style={{fontSize: 13.5, color: Grey7}} data-testid="project-customer-line">
                                        <span style={{color: Grey17}}>
                                            {projectCustomer?.company ?? customerEdge?.ChildName ?? customerEdge?.ChildObjectID}
                                        </span>
                                        {projectCustomer?.name && <span style={{marginLeft: 12}}>{projectCustomer.name}</span>}
                                        {projectCustomer?.email && <span style={{marginLeft: 12}}>{projectCustomer.email}</span>}
                                    </Typography>
                                )}
                                {record.Description && (
                                    <Typography style={{fontSize: 13.5, color: Grey7}}>{record.Description}</Typography>
                                )}
                            </div>
                        )}

                        {/* A failed action leaves the record loaded, so the message belongs here rather
                            than replacing the page. */}
                        {error && (
                            <Typography style={{fontSize: 13, color: "#B3261E", marginTop: 10}}>{error}</Typography>
                        )}

                        {/* Tab bar. Folds its tail into a menu on the right when the window is
                            too narrow for the row, and unfolds as it widens. */}
                        <OverflowTabs tabs={panes.map(p => ({key: p, label: labelFor(p)}))}
                                      selected={pane} onSelect={setPane} tabStyle={tabStyle}
                                      style={{borderBottom: `1px solid ${Grey2}`, marginTop: 20}}/>


                        {/* NOT ON A PROJECT: its Overview is the pipeline board ProjectMembers draws
                            for the "Detail" pane. What this card held for a folder is elsewhere --
                            the customer in the header, the owner on Visibility, the dates on
                            History. */}
                        {pane === "Detail" && !isProject && (
                            <div style={{marginTop: 24, display: "flex", flexDirection: "column", gap: 28}}>
                            {/* THE RECORD'S OWN TERRITORY. Without a boundary the fields, the
                                related-objects grid and whatever follows read as one undifferentiated
                                column, and nothing says where the record stops and its relationships
                                begin. Same Paper the rule page uses in t5, so a record looks like a
                                record in both apps.

                                NO TITLE INSIDE IT, unlike t5's: the page header directly above
                                already names this record, and repeating it under itself is a line
                                that tells the reader nothing they did not just read. */}
                            {/* boxSizing: border-box IS LOAD-BEARING. This app sets no global
                                box-sizing, so width:100% plus the card's own 40px of horizontal
                                padding renders 40px WIDER than the column it sits in -- the card
                                ran off the right edge of the window and its right border was never
                                drawn, which is the opposite of marking its territory. */}
                            <Paper elevation={1} style={{width: "100%", boxSizing: "border-box",
                                                         padding: "16px 20px 20px 20px",
                                                         display: "flex", flexDirection: "column", gap: 28}}>
                                <div style={{display: "flex", flexWrap: "wrap", gap: 40}}>
                                    {/* The identifier is what leaves this screen -- into an email,
                                        a change request, an auditor's question -- so it carries a
                                        copy control rather than asking to be read off and retyped. */}
                                    <Field label="Object ID" value={<CopyableValue text={record.ObjectID}/>}/>
                                    {/* NOT FOR A PROJECT. Ed, 2026-10-29: "These should not be
                                        shown since there is not object lifecycle and there will
                                        only be one Object type for project." A project is a
                                        folder that is OPEN or CLOSED: it has no revision chain,
                                        no approval and one object type, so Type, Project Type,
                                        Revision, Approval and the three lifecycle dates below
                                        would read "Project", "Project", "1" and four dashes on
                                        every one of them -- facts of the storage, not of the
                                        record. The Status pill stays: OPEN or CLOSED is the one
                                        state a folder has. */}
                                    {!isProject && (
                                        <Field label="Type" value={record.ObjectClassification}/>
                                    )}
                                    {/* THE OBJECT TYPE BY NAME -- Supply Agreement, Quality
                                        Agreement -- beside the classification. Ed, 2026-10-28,
                                        checking the source product's contract dialog field by
                                        field: its "Contract Type" is our object type, and this
                                        page said only "Contract". */}
                                    {!isProject && record.ObjectTypeName && (
                                        <Field label={`${record.ObjectClassification} Type`}
                                               value={record.ObjectTypeName}/>
                                    )}
                                    {!isProject && (
                                        <Field label="Revision" value={record.DisplayRevision ?? record.Revision}/>
                                    )}
                                    <Field label="Status" value={statusPill(record.Status)}/>
                                    {record.SeededFromRevisionUSID ? (
                                        <Field label="Form"
                                               value={seededForm
                                                   ? `${seededForm.ObjectID ?? seededForm.Name ?? "—"} REV `
                                                     + `${seededForm.DisplayRevision ?? seededForm.Revision ?? "—"}`
                                                   : null}/>
                                    ) : null}
                                    {!isProject && (
                                        <Field label="Approval" value={record.ApprovalStatus}/>
                                    )}
                                    {/* WHICH FOLDER THIS RECORD IS FILED IN, and the only control
                                        anywhere that puts one in. It renders for the four
                                        classifications a project holds and nothing else -- see
                                        FILEABLE -- so a Document or an Item never grows a control
                                        that would refuse on use.

                                        A FIELD AMONG THE FIELDS, NOT A TAB. The project is one
                                        fact about this record, and a tab holding one line is a
                                        door onto nothing. It reads the LOGICAL object rather than
                                        this revision, because membership keys on the object: a
                                        quote must not drop out of its project when somebody
                                        revises it. */}
                                    <ProjectFiling objectUSID={record.ObjectUSID}
                                                   memberType={fileableKind(record.ObjectClassification, record.ObjectTypeAcronym)}/>
                                    {/* THE QMS FOLDER IT IS FILED IN (2026-11-03): one folder each, and every record can be
                                        filed. A field among the fields, like the project above it. */}
                                    <RecordFolderField revisionUSID={record.USID}/>
                                    {/* THROUGH formatLocal, the helper the grids use, so a
                                        timestamp reads the same in the list you came from and the
                                        record you opened. Raw 2026-08-02T07:31:00.000Z is a
                                        transport format, not a reading surface. */}
                                    {!isProject && (
                                        <>
                                            <Field label="Released" value={formatLocal(record.ReleasedDatetime)}/>
                                            <Field label="Effective" value={formatLocal(record.EffectiveDatetime)}/>
                                            <Field label="Approved" value={formatLocal(record.ApprovedDatetime)}/>
                                        </>
                                    )}
                                </div>
                                <Field label="Description" value={record.Description}/>

                                {/* WHO THE AGREEMENT -- OR THE FOLDER -- IS WITH, read off the
                                    'Contract Customer' or 'Project Customer' edge the record
                                    document already carries (one edge, parent = this record,
                                    child = the customer). The source product asks it on
                                    the dialog; here it is a related record, made from the related
                                    pane below, and this is where it is READ. Absent until somebody
                                    makes the edge -- the sample tenant's contracts name their
                                    customer only in their title. */}
                                {customerEdge && (
                                    <Field label="Customer"
                                           value={`${customerEdge.ChildObjectID ? `${customerEdge.ChildObjectID} · ` : ""}${customerEdge.ChildName ?? "—"}`}/>
                                )}

                                {/* TRAINING IS A PROPERTY OF THE RECORD, so it sits with the record's
                                    other properties -- and this is the switch that reveals the Quizzes
                                    tab, which is why it cannot live inside it.

                                    DRAFT ONLY, because the server refuses a field edit outside a draft
                                    like any other; disabling it here means the page never offers a
                                    change that would come back refused. */}
                                <div style={{display: "flex", flexDirection: "column", gap: 1, minWidth: 220}}>
                                    <Typography style={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>
                                        Training
                                    </Typography>
                                    <div style={{display: "flex", alignItems: "center", gap: 8, minHeight: 21}}>
                                        <Tooltip title={String(record.Status ?? "").toUpperCase() === "DRAFT"
                                            ? "Whether people must be trained on this revision. Turning it on "
                                              + "adds the Quizzes tab, where the training is set up."
                                            : "Training is settled when a revision is released. Revise the "
                                              + "record to change it."}>
                                            <span style={{display: "inline-flex"}}>
                                                <Switch size="small"
                                                        checked={record.TrainingRequired === true}
                                                        disabled={savingTraining
                                                            || String(record.Status ?? "").toUpperCase() !== "DRAFT"}
                                                        onChange={event => setTrainingRequired(event.target.checked)}/>
                                            </span>
                                        </Tooltip>
                                        <Typography style={{fontSize: 15, fontWeight: 400, color: Grey17,
                                                            lineHeight: 1.45}}>
                                            {record.TrainingRequired === true
                                                ? "Training required"
                                                : "No training required"}
                                        </Typography>
                                    </div>
                                </div>

                                {/* WHO OWNS IT, AND WHEN IT HAPPENED -- the same two rows the rule
                                    page reads, in the same order. Every one of these was already in
                                    the payload and simply never rendered: the server joins the three
                                    owner NAMES beside their keys, so none of this needed a request.
                                    The position is the field that decides who can see the record at
                                    all, which makes it the odd one to have been missing. */}
                                <div style={{display: "flex", flexWrap: "wrap", gap: 40}}>
                                    <Field label="Primary Position" value={record.PrimaryPositionName}/>
                                    <Field label="Primary User Group" value={record.PrimaryUserGroupName}/>
                                    <Field label="Primary Work Group" value={record.PrimaryWorkGroupName}/>
                                </div>

                                {/* AND WHO ELSE CAN REACH IT. The primaries above are columns on the
                                    revision; these are membership rows, one-to-many, so they come
                                    from a read of their own. Read-only -- shares are managed on the
                                    Visibility tab, and this is here so that going there is needed
                                    less often. Draws nothing when the record is shared with nobody. */}
                                <SecondaryOwners objectRevisionUSID={usid} sessionContext={sessionContext}/>

                                <div style={{display: "flex", flexWrap: "wrap", gap: 40}}>
                                    <Field label="Created" value={formatLocal(record.CreatedDatetime)}/>
                                    <Field label="Modified" value={formatLocal(record.LastModifiedDatetime)}/>
                                </div>
                            </Paper>

                            {/* A TRAINING CURRICULUM'S BODY. On the Detail tab and not a tab of its own,
                                because this is the record: a curriculum with no objects and no audience
                                is an empty document, not a record with an empty tab. Every other
                                classification renders nothing here. */}
                            {effective.code === "TrainingCurriculum" ? (
                                <TrainingCurriculumPanel objectRevisionUSID={usid!}
                                                         sessionContext={sessionContext}
                                                         status={record.Status}/>
                            ) : null}

                            {/* THE FILES, BELOW THE RECORD (Ed, 2026-10-31). They used to be a tab of their
                                own, and a tab is a place a reader has to decide to go; the evidence a record
                                carries is part of reading the record, so it sits under the card. Every file,
                                grouped by where it hangs; the record's own files editable while the revision is
                                a draft, because attaching to a released revision would change what was released
                                -- the server decides that, this only stops offering it. */}
                            <div style={{maxWidth: 720}}>
                                <Attachments sourceUSID={usid!} allFiles
                                             recordLabel={`${record.ObjectID ?? ""} Rev ${record.DisplayRevision ?? record.Revision ?? ""}`.trim()}
                                             bundleName={`${record.ObjectID ?? "record"}-rev${record.DisplayRevision ?? record.Revision ?? ""}-attachments`}
                                             readOnly={String(record.Status ?? "").toUpperCase() !== "DRAFT"}/>
                            </div>
                            </div>
                        )}

                        {/* THE EDGES THIS RECORD OWNS. NOT MOUNTED UNTIL THE RECORD IS KNOWN:
                            PlatformTable queries on mount, and the effect that scopes the grid runs
                            when ObjectUSID resolves -- mounted early it drew every relationship in
                            the realm. The grid titles itself, with a count. */}
                        {pane === RELATED_PANE && (
                            <div style={{marginTop: 24}}>
                                {record?.ObjectUSID ? (
                                    <ObjectRelationshipTableApplet enterpriseComponent={relationshipEO?.getEnterpriseComponent("ObjectRelationship")}/>
                                ) : null}
                            </div>
                        )}
                        {/* WHAT POINTS AT THIS RECORD, which the other grid cannot show: it lists
                            the edges this object OWNS. Read only, because an edge belongs to the
                            record that created it. */}
                        {pane === RELATED_TO_PANE && (
                            <div style={{marginTop: 24}}>
                                {record?.ObjectUSID ? (
                                    <ObjectRelatedToTableApplet enterpriseComponent={relatedToEO?.getEnterpriseComponent("ObjectRelationship")}/>
                                ) : null}
                            </div>
                        )}

                        {pane === FORM_PANE && hasForm && (
                            <ObjectFormTab objectRevisionUSID={usid!} status={record.Status}/>
                        )}

                        {pane === SBOM_PANE && (
                            <SbomPane revisionUSID={usid}/>
                        )}

                        {/* READ-ONLY OUTSIDE DRAFT, decided here exactly as the Form and
                            Attachments panes decide it, and passed in rather than re-read: the
                            status is already loaded and a second opinion about it is a second
                            thing to get wrong. The SERVER is where the freeze is enforced
                            (CC-PQLN-FROZEN); this only avoids offering an edit it would refuse. */}
                        {/* READ-ONLY OUTSIDE DRAFT, decided here as the other content panes
                            decide it. Re-planning happens in a NEW revision, so an approved plan's
                            route is what somebody approved. */}
                        {pane === PLAN_PANE && (
                            <BuildPlanPane revisionUSID={usid}
                                           status={record.Status}
                                           readOnly={String(record.Status ?? "").toUpperCase() !== "DRAFT"}/>
                        )}

                        {/* NO readOnly. This pane offers no action at all -- it is a report, and
                            every refusal it could describe belongs to the assignment endpoints. A
                            draft and an approved plan are asked the same question. */}
                        {pane === PLAN_WORKFORCE_PANE && (
                            <BuildPlanWorkforcePane revisionUSID={usid}/>
                        )}

                        {/* THE OBJECT, NOT THE REVISION, is what everything here hangs off: a
                            quality record is raised once and WORKED rather than revised, so its
                            workflow, its links and its history are all keyed on the logical
                            object. The revision travels too, but only so the pane can ask what
                            privileges the caller holds ON THIS RECORD.

                            AND NO readOnly PROP. Whether this record can be acted on is its
                            WORKFLOW state, not the revision's status, and the server says so on
                            the record itself -- re-deriving it here is how two screens come to
                            disagree. */}
                        {/* THE OBJECT, like the quality record: a questionnaire is filed once
                            and answered, and its questions, responses and answers all hang off the
                            logical object rather than a revision. */}
                        {/* THE REVISION, unlike the quality record and the questionnaire: a
                            contract's lines, milestones and payments are keyed on the REVISION,
                            because repricing in a new revision must not retrospectively change
                            what the performed one said. */}
                        {/* THE OBJECT. A supplier's paperwork follows the SUPPLIER, not one
                            revision of its record: a certificate does not stop being on file
                            because somebody revised the supplier's address. */}
                        {pane === SUPPLIER_PANE && (
                            <SupplierDocumentsPane objectUSID={record.ObjectUSID}/>
                        )}

                        {pane === CONTRACT_PANE && (
                            <ContractSchedulePane revisionUSID={usid} status={record?.Status}/>
                        )}
                        {pane === INVENTORY_LOTS_PANE && effective.code === "Inventory" && record?.ObjectUSID && (
                            <InventoryLotsPane objectUSID={record.ObjectUSID} status={record?.Status}
                                               canEdit={hasPrivilege(objectPrivilegeCode(effective, "edit"))}/>
                        )}
                        {pane === ORDER_RECEIPTS_PANE && effective.code === "PurchaseOrder" && record?.ObjectUSID && (
                            <OrderReceiptsPane objectUSID={record.ObjectUSID}/>
                        )}
                        {pane === ITEM_BOM_PANE && effective.code === "Item" && record?.ObjectUSID && (
                            <ItemBillOfMaterialsPane objectUSID={record.ObjectUSID} status={record?.Status}
                                                     canEdit={hasPrivilege(objectPrivilegeCode(effective, "edit"))}/>
                        )}
                        {pane === ITEM_SUPPLIERS_PANE && effective.code === "Item" && record?.ObjectUSID && (
                            <ItemSuppliersPane objectUSID={record.ObjectUSID}/>
                        )}
                        {pane === RECEIPT_LOTS_PANE && effective.code === "Receivable" && record?.ObjectUSID && (
                            <ReceiptLotsPane objectUSID={record.ObjectUSID} status={record?.Status}
                                             canCreate={hasPrivilege("lot:create")}/>
                        )}

                        {/* THE OBJECT, not the revision. The pane resolves the current revision
                            itself, so a link somebody saved keeps working after a revise -- and
                            the fields it shows ARE the revision's, which is why a regulatory
                            class recorded on one does not rewrite what an approved one claimed. */}
                        {pane === DEVELOPMENT_PANE && (
                            <DevelopmentPane objectUSID={record.ObjectUSID}/>
                        )}

                        {pane === QUESTIONNAIRE_PANE && (
                            <QuestionnairePane objectUSID={record.ObjectUSID}
                                               revisionUSID={usid}/>
                        )}

                        {pane === QUALITY_PANE && (
                            <QualityRecordPane objectUSID={record.ObjectUSID}
                                               revisionUSID={usid}/>
                        )}

                        {pane === QUOTE_PANE && (<>
                            {/* THE OFFER FIRST -- what it answers, the three questions, who it is
                                shared with, who has signed, what is built from it -- and the lines
                                it is priced on below. See QuoteOfferPane. */}
                            <QuoteOfferPane revisionUSID={String(usid)} objectUSID={record.ObjectUSID}
                                            status={record.Status} objectID={record.ObjectID}
                                            refreshToken={quoteTick}/>
                            <QuoteLinesPane revisionUSID={usid}
                                            status={record.Status}
                                            readOnly={String(record.Status ?? "").toUpperCase() !== "DRAFT"}
                                            onChanged={() => setQuoteTick(tick => tick + 1)}/>
                            {record?.ObjectUSID && (
                                <Box sx={{marginTop: "18px"}}>
                                    <DiscussionPane objectUSID={record.ObjectUSID}/>
                                </Box>
                            )}
                        </>)}
                        {/* THE OBJECT, NOT THE REVISION: the satellite keys on the revision, but a
                            request has no revise and the list resolves the one row behind the
                            logical object -- the same key its project membership holds. */}
                        {pane === REQUEST_PANE && (
                            <QuoteRequestPane objectUSID={record.ObjectUSID}/>
                        )}
                        {/* THE LOT, NOT THE REVISION, is what a release names: a lot revised after
                            shipment is the same lot and the customer's access must not follow the
                            revision. The revision travels too, but only so the pane can ask what
                            privileges the caller holds ON THIS RECORD. */}
                        {pane === CUSTOMERS_PANE && (
                            <CustomerReleasesPane lotObjectUSID={record.ObjectUSID}
                                                  revisionUSID={usid}
                                                  lotName={record.Name}/>
                        )}
                        {pane === PARKOUR_PANE && (
                            <ParkourDocuments objectRevisionUSID={usid!} sessionContext={sessionContext}/>
                        )}

                        {pane === QUIZ_PANE && (
                            <QuizzesTab objectRevisionUSID={usid!} sessionContext={sessionContext}
                                        onChanged={() => setReloadTick(t => t + 1)}/>
                        )}

                        {pane === "Visibility" && (
                            <div style={{marginTop: 24}}>
                                <ObjectVisibilityTab objectRevisionUSID={usid!} sessionContext={sessionContext}
                                                     onPrimaryChanged={() => setReloadTick(t => t + 1)}/>
                            </div>
                        )}

                        {/* ALWAYS OFFERED, EVEN WHEN NOTHING IS REQUIRED. A tab that appears only
                            once approvals are configured would answer "who has to sign this?" with
                            silence in exactly the case where the answer matters most -- nobody, and
                            it will release unwitnessed. The tab says so in words. */}
                        {pane === "Approvals" && (
                            <div style={{marginTop: 4, marginBottom: 40}}>
                                <ApprovalTab targetType="Object" recordUSID={usid!}
                                             recordStatus={record.Status}
                                             onChanged={() => setReloadTick(t => t + 1)}/>
                            </div>
                        )}

                        {pane === "Permissions" && (
                            <div style={{marginTop: 24, marginBottom: 40}}>
                                {/* One block per privilege, exactly as Term and Rule render it: who grants
                                    it (role, and whether directly or through a capability), who holds those
                                    roles, and the per-user DENY constraints scoped to THIS record.

                                    Privileges are per CLASSIFICATION -- a Document is guarded as 'Document'
                                    and an Equipment as 'Equipment' -- so the block list is derived from the
                                    record's own classification rather than hardcoded. */}
                                {objectPrivileges.map((privilegeName) => (
                                    <PermissionConfiguration key={privilegeName} name={privilegeName} parentUSID={usid!}
                                                             sessionContext={sessionContext}
                                                             roles={rolesByPrivilege[privilegeName]}
                                                             rolesLoaded={rolesLoaded} rolesFailed={rolesFailed}/>
                                ))}
                            </div>
                        )}

                        {pane === "History" && (
                            <div style={{marginTop: 24}}>
                                {/* Same reason as the relationships grid above: an unscoped mount
                                    query would show every audit row in the realm. */}
                                {record?.ObjectUSID ? (
                                    <ObjectAuditHistoryTableApplet enterpriseComponent={auditEO?.getEnterpriseComponent("ObjectAuditHistory")}/>
                                ) : null}
                            </div>
                        )}
                        {/* WHAT IS FILED IN THIS FOLDER, BELOW THE PANE AND NOT ABOVE IT. Ed moved
                            it here on 2026-10-27: the record's own fields are what the page is
                            for, and a row of tabs between the strip and the Detail card pushed the
                            record down and read as though the folder's contents were the record.
                            Below, it is a second section you arrive at after reading the project.

                            STILL ONE SELECTION ACROSS BOTH ROWS -- choosing a kind is choosing a
                            pane, so when a kind is open no pane body renders above and this is the
                            only thing on the page, which is what that choice should show.
                            ProjectMembers draws the row and owns the one fetch the counts come
                            from; `null` on any other pane leaves the row and drops the list. */}
                        {isProject && record?.ObjectUSID && (
                            <ProjectMembers projectObjectUSID={String(record.ObjectUSID)}
                                            status={record.Status}
                                            customerObjectUSID={customerEdge?.ChildUSID ?? null}
                                            api={api}
                                            tab={projectTabs.includes(pane) || pane === "Detail" ? pane : null}
                                            onCounts={setMemberCounts}/>
                        )}
                        {/* A FOLDER'S FILES, under its overview and only there: the tab that used to hold
                            them is gone (2026-10-31), and a kind's pane is that kind alone. */}
                        {isProject && pane === "Detail" && record?.ObjectUSID && (
                            <div style={{marginTop: 24, maxWidth: 720}}>
                                <Attachments sourceUSID={usid!} allFiles recordLabel={record.ObjectID ?? undefined}
                                             bundleName={`${record.ObjectID ?? "project"}-attachments`}
                                             readOnly={String(record.Status ?? "").toUpperCase() !== "DRAFT"}/>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>

        {/* DOES THIS CHANGE REQUIRE PEOPLE TO RETRAIN? Asked only of a record whose Require Retraining is
            on, and only at release. Neither answer is the default: saying yes puts everyone trained on an
            earlier revision back to outstanding, saying no leaves their training standing, and the
            difference between a rewritten procedure and a corrected typo is a judgement the person
            releasing is making anyway. */}
        {/* ── when does it start governing? ──────────────────────────────────────────────── */}
        <Dialog open={editing} onClose={() => !savingEdit && setEditing(false)} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>Edit this {effective.singular.toLowerCase()}</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "18px",
                                paddingTop: "8px !important"}}>
                <TextField autoFocus size="small" label="Name" value={draftTitle}
                           disabled={savingEdit}
                           onChange={event => setDraftTitle(event.target.value)}/>
                <TextField size="small" label="Description" value={draftDescription} multiline
                           minRows={3} disabled={savingEdit}
                           onChange={event => setDraftDescription(event.target.value)}/>
                {/* THE IDENTIFIER IS NOT HERE and the reader is told why, rather than left to
                    wonder whether the field was forgotten. */}
                <Typography style={{fontSize: 12.5, color: Grey7, lineHeight: 1.6}}>
                    The identifier, the revision and the dates are not edited here. An identifier
                    somebody has already quoted must go on naming the same thing.
                </Typography>
                {editRefusal && (
                    <Typography style={{fontSize: 12.5, color: "#B3261E", lineHeight: 1.6}}>
                        {editRefusal}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button size="small" disabled={savingEdit} sx={{textTransform: "none"}}
                        onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation
                        disabled={!draftTitle.trim() || savingEdit}
                        sx={{textTransform: "none"}} onClick={saveEdit}>
                    {savingEdit ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>

        {/* NAMES THE TAB, because "you have unsaved changes" on a record with six tabs is a
            riddle. And it does not offer to save FOR them: what Save means differs per panel, and
            a button that silently saved a half-filled form would be a worse surprise than the one
            this replaces. Go back, or go on knowingly. */}
        <Dialog open={!!unsavedWarning} onClose={() => setUnsavedWarning(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Unsaved changes</DialogTitle>
            <DialogContent>
                <Typography style={{fontSize: 14}}>
                    {unsavedWarning?.tabs.length === 1
                        ? <>The <strong>{unsavedWarning?.tabs[0]}</strong> tab has changes that have not been saved.</>
                        : <>These tabs have changes that have not been saved: <strong>{unsavedWarning?.tabs.join(", ")}</strong>.</>}
                    {" "}
                    {unsavedWarning?.action === "Revise"
                        ? "Revising opens a new draft, so they would be lost against a revision that no longer exists."
                        : `Continuing with ${unsavedWarning?.action} uses what is on the server and leaves them behind.`}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" sx={{textTransform: "none"}}
                        onClick={() => setUnsavedWarning(null)}>Go back and save</Button>
                <Button sx={{textTransform: "none"}}
                        onClick={() => {
                            const parked = unsavedWarning;
                            setUnsavedWarning(null);
                            if (parked) performLifecycleAction(parked.action,
                                                               {...parked.extra, __unsavedAcknowledged: true});
                        }}>
                    Continue without saving
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={askingEffective} onClose={() => setAskingEffective(false)} maxWidth="sm" fullWidth>
            <DialogTitle style={{fontSize: 20, fontWeight: 700, color: Grey17}}>
                Release this revision
            </DialogTitle>
            <DialogContent>
                <Typography style={{fontSize: 14, color: Grey17}}>
                    Releasing makes this the official revision straight away. You can also say it does
                    not start <i>governing</i> until a later date — the gap is the window people have to
                    be trained on it.
                </Typography>
                <Typography style={{fontSize: 13, color: Grey5, marginTop: 14, marginBottom: 6}}>
                    In force from
                </Typography>
                <TextField size="small" type="date" value={effectiveChoice}
                           onChange={event => setEffectiveChoice(event.target.value)}
                           slotProps={{htmlInput: {min: localToday()}}}
                           sx={{minWidth: 200}}/>
                <Typography style={{fontSize: 12, color: Grey5, marginTop: 8}}>
                    {effectiveChoice
                        ? "Anyone who must train on this has until that date."
                        : "Leave empty to have it govern immediately."}
                </Typography>
            </DialogContent>
            <DialogActions style={{padding: "12px 24px 20px"}}>
                <Button onClick={() => setAskingEffective(false)} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disabled={!!busyAction}
                        onClick={() => {
                            setAskingEffective(false);
                            // "YYYY-MM-DD HH:MM:SS", NOT toISOString(). The server's date parser takes
                            // four formats and this is the one it reads LITERALLY -- no timezone
                            // conversion at either end, which is what the naive DB-local columns want.
                            // toISOString() would convert local midnight to UTC, and east of Greenwich
                            // that lands on the PREVIOUS day: an effective date off by one, silently.
                            const chosen = effectiveChoice ? `${effectiveChoice} 00:00:00` : undefined;
                            performLifecycleAction("Release",
                                {__effectiveAsked: true, ...(chosen ? {EffectiveDatetime: chosen} : {})});
                        }}
                        sx={{textTransform: "none"}}>
                    {effectiveChoice ? "Release, in force from that date" : "Release now"}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={askingRetrain} onClose={() => setAskingRetrain(false)} maxWidth="sm" fullWidth>
            <DialogTitle style={{fontSize: 20, fontWeight: 700, color: Grey17}}>
                Does this change require people to retrain?
            </DialogTitle>
            <DialogContent>
                <Typography style={{fontSize: 14, color: Grey17}}>
                    This record requires retraining, so releasing this revision decides what happens to the
                    training people have already done on it.
                </Typography>
                <Typography style={{fontSize: 13, color: Grey5, marginTop: 12}}>
                    <strong>Yes</strong> — anyone trained on an earlier revision must take it again.<br/>
                    <strong>No</strong> — the change is administrative, and their training still stands.
                </Typography>
            </DialogContent>
            <DialogActions style={{padding: "12px 24px 20px"}}>
                <Button onClick={() => setAskingRetrain(false)} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="outlined" disabled={!!busyAction}
                        onClick={() => { setAskingRetrain(false);
                                         performLifecycleAction("Release", {...pendingRelease, TrainingResetRequired: false}); }}
                        sx={{textTransform: "none"}}>
                    No — administrative
                </Button>
                <Button variant="contained" disabled={!!busyAction}
                        onClick={() => { setAskingRetrain(false);
                                         performLifecycleAction("Release", {...pendingRelease, TrainingResetRequired: true}); }}
                        sx={{textTransform: "none"}}>
                    Yes — retrain
                </Button>
            </DialogActions>
        </Dialog>
        </div>
    );
}
